// CustomizedTable.tsx
import { Box, Typography } from '@mui/material';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { memo } from 'react';
import { useTheme } from '@mui/material/styles';
import { UseColumnFiltersResult } from 'components/ColumnFilters/useColumnFilters';

interface CustomizedTableProps {
  tableTitle: string;
  children?: React.ReactNode;
  childrenContainerProps?: any;
  isChildren?: boolean;
  tableData: any[];
  total: number;
  isTotalCountLoading?: boolean;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSorting: (sortInfo: { sortBy: string; sortDir: string }) => void;
  sortingInfo: { sortBy: string; sortDir: string };
  defaultColumns?: string[];
  tableType?: string;
  columns: any[];
  groupBy?: string;
  onGroupByChange?: (groupBy: string) => void;
  includeGroupByMenuColumns?:string[];
  hasGroupBy?: boolean;
  onMetadataUpdatesClick?: () => void;
  metadataButtonLabel?: string;
  showExport?: boolean;
  exportTableData?: (...args: any[]) => Promise<any> | any;
  fileName?: string;
  exportColumns?: any[];
  noRowsFallback?: React.ReactNode;
  isFetching?: boolean;
  rowHeight?: number;
  pageSizeOptions?: number[];
  isRenderPagination?: boolean;
  isPrevNextPagination?: boolean;
  enableColumnFilter?: boolean;
  columnFilterController?: UseColumnFiltersResult;
  getColumnFilterValues?: (field: string) => Promise<any[]>;
  isSortIcon?: boolean;
}

const CustomizedTable = memo(({
  tableTitle,
  tableData,
  total,
  isTotalCountLoading = false,
  page = 1,
  pageSize = 10,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  onSorting,
  sortingInfo,
  defaultColumns,
  tableType,
  columns,
  groupBy,
  onGroupByChange,
  includeGroupByMenuColumns,
  hasGroupBy = true,
  onMetadataUpdatesClick,
  metadataButtonLabel,
  showExport,
  exportTableData,
  fileName,
  exportColumns,
  noRowsFallback,
  isFetching = false,
  rowHeight,
  pageSizeOptions = [10, 50, 100, 200, 500, 1000],
  isRenderPagination = true,
  isPrevNextPagination = false,
  children,
  childrenContainerProps,
  isChildren,
  enableColumnFilter = false,
  columnFilterController,
  getColumnFilterValues,
  isSortIcon = false,
}: CustomizedTableProps) => {
  const theme = useTheme();
  const p = theme.palette;
  const isCustom = (tableType === 'IBP GDO Details') ? 'h5' : 'h4';
  const isReconciliationTable = tableType?.toLowerCase().startsWith('data-reconciliation-');
  const customText = isReconciliationTable ? 'h6' : isCustom;
  const effectiveHeaderRowHeight = isReconciliationTable ? 44 : undefined;

  return (
    <Box
      sx={{
        '& .rdg-cell': {
          borderInlineEnd: '1px solid #fff !important',
          borderBlockEnd: '1px solid #fff !important',
          paddingTop: '16px !important',
          paddingBottom: '16px !important',
        },
        '& .rdg-row .rdg-cell.reconciliation-run-date-cell': {
          whiteSpace: 'pre-line',
          lineHeight: '16px !important',
          paddingTop: '10px !important',
          paddingBottom: '10px !important',
          display: 'flex !important',
          alignItems: 'center !important',
        },
        '& .rdg-row .rdg-cell.reconciliation-cell': {
          alignContent: 'center !important',
        },
        '& .rdg-header-row .rdg-cell': {
          borderInlineEnd: '1px solid #fff !important',
          borderBlockEnd: '1px solid #fff !important',
          ...(isReconciliationTable && {
            display: 'flex !important',
            alignItems: 'center !important',
            overflow: 'hidden',
            whiteSpace: 'normal !important',
            lineHeight: '16px !important',
            textAlign: 'left !important',
            '& > div': {
              display: 'flex !important',
              width: '100% !important',
              minWidth: 0,
              overflow: 'hidden',
            },
            '& .filter-icon, & .sort-icon': {
              display: 'flex !important',
              alignItems: 'center !important',
              width: '100% !important',
              minWidth: 0,
              overflow: 'hidden',
              textAlign: 'left !important',
            },
            '& .filter-icon > span:not([role="button"]), & .sort-icon > span:not([role="button"])': {
              display: '-webkit-box !important',
              color:'#e5e5e5 !important',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              flex: '1 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal !important',
              lineHeight: '16px !important',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              textAlign: 'left !important',
            },
            '& .filter-icon > span[role="button"], & .sort-icon > span[role="button"]': {
              flex: '0 0 auto',
              flexShrink: 0,
              marginLeft: 'auto',
              paddingLeft: '4px',
              display: 'flex !important',
              alignItems: 'center !important',
            },
            '& .filter-icon img, & .sort-icon img': {
              float: 'none !important',
              marginTop: '0 !important',
              verticalAlign: 'middle',
            },
          }),
        },
        '& .rdg-row': {
          borderBlockEnd: '1px solid #fff !important',
        },
        '& .status-center': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        },
      }}
    >
      <CustomizedDataGrid
        title={
          <Typography variant={customText} sx={{ color: p.primary.main, fontWeight: 500 }}>
            <HighlightedText text={tableTitle} />
          </Typography>
        }
        childrenContainerProps={childrenContainerProps}
        tableColumns={columns}
        tableData={tableData}
        tableType={tableType}
        defaultColumnNames={defaultColumns}
        listLength={total}
        isTotalCountLoading={isTotalCountLoading}
        page={page}
        pageSize={pageSize}
        paginationData={{ totalElements: total, totalPages: Math.ceil(total / pageSize) }}
        onSorting={onSorting}
        currSorting={sortingInfo}
        handlePageChange={(_: any, p: number) => onPageChange(p)}
        handlepageSizeChange={(e: any) => onPageSizeChange(Number.parseInt(e.target.value))}
        isDateRangeFilter={false}
        isDisabled={false}
        isShowMoreButton={true}
        pageSizeOptions={pageSizeOptions}
        isRenderPagination={isRenderPagination}
        isGroupBy={hasGroupBy}
        onGroupByChange={onGroupByChange}
        groupBy={groupBy}
        includeGroupByMenuColumns={includeGroupByMenuColumns}
        onMetadataUpdatesClick={onMetadataUpdatesClick}
        metadataButtonLabel={metadataButtonLabel}
        showExport={showExport}
        exportTableData={exportTableData}
        fileName={fileName}
        exportColumns={exportColumns}
        noRowsFallback={noRowsFallback}
        isChildren={isChildren}
        isFetching={isFetching}
        rowHeight={rowHeight}
        headerRowHeight={effectiveHeaderRowHeight}
        enableColumnFilter={enableColumnFilter}
        columnFilterController={columnFilterController}
        getColumnFilterValues={getColumnFilterValues}
        isPrevNextPagination = {isPrevNextPagination}
        isSortIcon={isSortIcon}
      >
        {children}
      </CustomizedDataGrid>
    </Box>
  );
});

export default CustomizedTable;